(() => {
  const GATE_SELECTOR = '[data-gr8bake-age-gate]';

  function isVerified(storageKey) {
    try {
      const storedValue = localStorage.getItem(storageKey);

      if (!storedValue) {
        return false;
      }

      const verification = JSON.parse(storedValue);

      if (
        verification &&
        verification.verified === true &&
        Number(verification.expiresAt) > Date.now()
      ) {
        return true;
      }

      localStorage.removeItem(storageKey);

      return false;
    } catch (error) {
      return false;
    }
  }

  function saveVerification(storageKey, rememberDays) {
    try {
      const days = Number(rememberDays) || 30;

      const expiresAt =
        Date.now() + days * 24 * 60 * 60 * 1000;

      localStorage.setItem(
        storageKey,
        JSON.stringify({
          verified: true,
          expiresAt: expiresAt
        })
      );
    } catch (error) {
      /*
        If localStorage is unavailable,
        allow access for the current page.
      */
    }
  }

  function lockPage() {
    document.documentElement.classList.add(
      'gr8bake-age-gate-open'
    );

    document.body.classList.add(
      'gr8bake-age-gate-open'
    );
  }

  function unlockPage() {
    document.documentElement.classList.remove(
      'gr8bake-age-gate-open'
    );

    document.body.classList.remove(
      'gr8bake-age-gate-open'
    );
  }

  function getFocusableElements(gate) {
    return Array.from(
      gate.querySelectorAll(
        [
          'button:not([disabled]):not([hidden])',
          'a[href]:not([hidden])',
          'input:not([disabled]):not([hidden])',
          'select:not([disabled]):not([hidden])',
          'textarea:not([disabled]):not([hidden])'
        ].join(',')
      )
    ).filter((element) => {
      return element.offsetParent !== null;
    });
  }

  function initializeGate(gate) {
    if (!gate) {
      return;
    }

    if (gate.dataset.initialized === 'true') {
      return;
    }

    gate.dataset.initialized = 'true';

    const storageKey =
      gate.dataset.storageKey ||
      'gr8bake_age_verified_v1';

    const rememberDays =
      Number(gate.dataset.rememberDays) || 30;

    const designMode =
      gate.dataset.designMode === 'true';

    const exitUrl =
      gate.dataset.exitUrl || '';

    const entryState =
      gate.querySelector('[data-age-gate-entry]');

    const deniedState =
      gate.querySelector('[data-age-gate-denied]');

    const yesButton =
      gate.querySelector('[data-age-yes]');

    const noButton =
      gate.querySelector('[data-age-no]');

    const leaveButton =
      gate.querySelector('[data-age-leave]');

    /*
      In Shopify Theme Editor:
      always show the age gate so it can be edited.
    */
    if (designMode) {
      document.documentElement.classList.remove(
        'gr8bake-age-verified'
      );

      gate.style.display = 'flex';

      lockPage();
    } else if (isVerified(storageKey)) {
      document.documentElement.classList.add(
        'gr8bake-age-verified'
      );

      gate.style.display = 'none';

      unlockPage();

      return;
    } else {
      document.documentElement.classList.remove(
        'gr8bake-age-verified'
      );

      gate.style.display = 'flex';

      lockPage();
    }

    requestAnimationFrame(() => {
      yesButton?.focus();
    });

    /*
      ========================================
      YES — VISITOR IS 18+
      ========================================
    */

    yesButton?.addEventListener('click', () => {
      if (!designMode) {
        saveVerification(
          storageKey,
          rememberDays
        );

        document.documentElement.classList.add(
          'gr8bake-age-verified'
        );
      }

      gate.classList.add('is-closing');

      window.setTimeout(() => {
        gate.style.display = 'none';

        gate.classList.remove('is-closing');

        unlockPage();
      }, 180);
    });

    /*
      ========================================
      NO — VISITOR IS UNDER 18
      ========================================
    */

    noButton?.addEventListener('click', () => {
      if (entryState) {
        entryState.hidden = true;
      }

      if (deniedState) {
        deniedState.hidden = false;
      }

      requestAnimationFrame(() => {
        leaveButton?.focus();
      });
    });

    /*
      ========================================
      GO BACK
      ========================================

      1. If a custom redirect URL was entered
         in Theme Editor, use that.

      2. Otherwise go back to the visitor's
         previous page.

      3. If there is no previous page, keep
         them on the denied screen.
    */

    leaveButton?.addEventListener('click', () => {
      if (exitUrl) {
        window.location.href = exitUrl;
        return;
      }

      if (window.history.length > 1) {
        window.history.back();
        return;
      }

      /*
        No previous page exists.

        Do nothing and keep the visitor
        on the blocked under-18 screen.
      */
    });

    /*
      ========================================
      KEYBOARD ACCESSIBILITY
      ========================================
    */

    document.addEventListener(
      'keydown',
      (event) => {
        if (gate.style.display === 'none') {
          return;
        }

        /*
          Escape should NOT dismiss the gate.
        */
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();

          return;
        }

        /*
          Keep keyboard focus inside the gate.
        */
        if (event.key !== 'Tab') {
          return;
        }

        const focusableElements =
          getFocusableElements(gate);

        if (!focusableElements.length) {
          return;
        }

        const firstElement =
          focusableElements[0];

        const lastElement =
          focusableElements[
            focusableElements.length - 1
          ];

        if (
          event.shiftKey &&
          document.activeElement === firstElement
        ) {
          event.preventDefault();

          lastElement.focus();

          return;
        }

        if (
          !event.shiftKey &&
          document.activeElement === lastElement
        ) {
          event.preventDefault();

          firstElement.focus();
        }
      },
      true
    );
  }

  function initializeAllGates() {
    document
      .querySelectorAll(GATE_SELECTOR)
      .forEach(initializeGate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeAllGates
    );
  } else {
    initializeAllGates();
  }

  /*
    Shopify Theme Editor reload support.
  */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      const gate =
        event.target.querySelector(
          GATE_SELECTOR
        );

      if (!gate) {
        return;
      }

      gate.dataset.initialized = 'false';

      initializeGate(gate);
    }
  );
})();